import axios from 'axios';
import { BACKEND_URL, apiPath } from '../../../config/apiBase';

export { BACKEND_URL };
export const API = apiPath('/api/tickets');

export const authHeaders = () => ({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export const authConfig = (params = {}) => ({
  ...authHeaders(),
  params,
});

/** Flatten FastAPI/axios error payloads into a safe string for React UI. */
export function formatApiError(err, fallback = 'Request failed') {
  const detail = err?.response?.data?.detail ?? err?.message;
  if (detail == null || detail === '') return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item?.msg) {
          const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : '';
          return loc ? `${loc}: ${item.msg}` : item.msg;
        }
        return JSON.stringify(item);
      })
      .join('; ');
  }
  if (typeof detail === 'object') {
    if (detail.msg) return detail.msg;
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  return String(detail);
}

export const STATUS_COLORS = {
  open: '#3B82F6',
  in_progress: '#F59E0B',
  pending: '#8B5CF6',
  completed: '#10B981',
  closed: '#6B7280',
  reopen: '#EF4444',
  cancelled: '#9CA3AF',
};

export const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Pending',
  completed: 'Completed',
  closed: 'Closed',
  reopen: 'Reopen',
  cancelled: 'Cancelled',
};

export const EDITABLE_TICKET_STATUSES = new Set(['open']);
/** Manual uploads while work is active (matches backend ATTACHMENT_UPLOAD_STATUSES). */
export const ATTACHMENT_UPLOAD_STATUSES = new Set(['open', 'in_progress', 'pending', 'reopen']);

export function isTicketEditable(status) {
  return EDITABLE_TICKET_STATUSES.has(status);
}

export function canUploadTicketAttachment(status) {
  return ATTACHMENT_UPLOAD_STATUSES.has(status);
}

export const DOMAIN_LABELS = {
  hardware: 'Hardware',
  software: 'Software',
  product: 'App Support',
};

export const DOMAIN_COLORS = {
  hardware: '#8B5CF6',
  software: '#3B82F6',
  product: '#10B981',
};

/** Shared inline styles — aligned with tickets.css tokens for pages outside .tickets-shell too. */
export const fieldStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #CBD5E1',
  borderRadius: '8px',
  fontSize: '12px',
  fontFamily: 'inherit',
  background: '#FFFFFF',
  color: '#0F172A',
};

export const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '4px',
  letterSpacing: '0.01em',
};

export const thStyle = {
  padding: '7px 10px',
  fontWeight: '600',
  color: '#475569',
  fontSize: '11px',
  whiteSpace: 'nowrap',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
};
export const tdStyle = {
  padding: '7px 10px',
  color: '#0F172A',
  fontSize: '12px',
  verticalAlign: 'middle',
};

export const pageTitleStyle = {
  fontSize: '15px',
  fontWeight: '600',
  margin: '0 0 2px',
  letterSpacing: '-0.02em',
  color: '#0F172A',
};

export const pageSubtitleStyle = {
  color: '#94A3B8',
  margin: '0 0 8px',
  fontSize: '12px',
};

export const btnPrimary = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  padding: '7px 14px',
  background: '#002FA7',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontWeight: '600',
  cursor: 'pointer',
  fontSize: '12px',
  fontFamily: 'inherit',
  boxShadow: '0 1px 2px rgba(0, 47, 167, 0.2)',
};

export const btnSecondary = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  padding: '7px 14px',
  background: 'white',
  color: '#475569',
  border: '1px solid #CBD5E1',
  borderRadius: '8px',
  fontWeight: '600',
  cursor: 'pointer',
  fontSize: '12px',
  fontFamily: 'inherit',
};

export const alertError = {
  padding: '10px 12px',
  background: '#FEF2F2',
  color: '#B91C1C',
  borderRadius: '8px',
  marginBottom: '10px',
  fontSize: '12px',
  border: '1px solid #FECACA',
};

export const alertSuccess = {
  padding: '10px 12px',
  background: '#ECFDF5',
  color: '#059669',
  borderRadius: '8px',
  marginBottom: '10px',
  fontSize: '12px',
  border: '1px solid #A7F3D0',
};

export const alertWarning = {
  padding: '10px 12px',
  background: '#FFFBEB',
  color: '#B45309',
  borderRadius: '8px',
  marginBottom: '10px',
  fontSize: '12px',
  border: '1px solid #FDE68A',
};

export async function fetchTicket(ticketNo) {
  const res = await axios.get(`${API}/${ticketNo}`, authHeaders());
  return res.data;
}

/** Parse list response + optional X-Total-Count header from GET /tickets/. */
export function parseTicketListResponse(res) {
  const items = Array.isArray(res?.data) ? res.data : [];
  const raw = res?.headers?.['x-total-count'] ?? res?.headers?.['X-Total-Count'];
  const parsed = raw != null && raw !== '' ? Number(raw) : NaN;
  const total = Number.isFinite(parsed) ? parsed : items.length;
  return { items, total };
}

export const TICKET_LIST_PAGE_SIZE = 20;

export async function fetchTicketComments(ticketNo) {
  const res = await axios.get(`${API}/${ticketNo}/comments`, authHeaders());
  return res.data;
}

export async function fetchDuplicates(params) {
  const res = await axios.get(`${API}/duplicates`, authConfig(params));
  return res.data;
}

/** Trigger browser download of a JS object as a pretty-printed JSON file. */
export function downloadJson(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Trigger browser download of CSV text. */
export function downloadCsvExport(filename, csvContent) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Download CSV from backend export endpoint. */
export async function downloadTicketsCsvExport(params = {}) {
  const token = localStorage.getItem('token');
  const res = await axios.get(`${API}/reports/export.csv`, {
    withCredentials: true,
    headers: { Authorization: `Bearer ${token}` },
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tickets-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Open print dialog for Save as PDF (browser print). Optionally clone a DOM node. */
export function printReportAsPdf(title, summaryLines = [], sourceEl = null) {
  const w = window.open('', '_blank');
  if (!w) return;
  const summaryHtml = summaryLines
    .map((l) => `<p><strong>${l.label}:</strong> ${l.value}</p>`)
    .join('');
  const bodyHtml = sourceEl
    ? sourceEl.innerHTML
    : `${summaryHtml}<p>Generated: ${new Date().toLocaleString()}</p>`;
  w.document.write(`
    <html><head><title>${title}</title>
    <style>
      body{font-family:sans-serif;padding:24px;color:#111}
      h1,h2,h3{margin:12px 0 8px}
      table{border-collapse:collapse;width:100%;margin-top:12px}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:11px}
      th{background:#f3f4f6}
      a{color:#002FA7;text-decoration:none}
      button,input,select,textarea,.tk-btn{display:none!important}
    </style>
    </head><body>
    <h1>${title}</h1>
    ${sourceEl ? summaryHtml : ''}
    ${bodyHtml}
    </body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 250);
}

export function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) return rem ? `${h}h ${m}m ${rem}s` : `${h}h ${m}m`;
  return rem ? `${m}m ${rem}s` : `${m}m`;
}

/** Download member activity CSV from backend. */
export async function downloadMemberReportCsv(params = {}) {
  const token = localStorage.getItem('token');
  const res = await axios.get(`${API}/reports/member/export.csv`, {
    withCredentials: true,
    headers: { Authorization: `Bearer ${token}` },
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `member-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function ticketAssigneeLabel(ticket) {
  const names = ticket.assignee_names;
  if (Array.isArray(names) && names.length > 0) return names.join(', ');
  // Fallback if names were not enriched — never show a bare "Assigned" status word.
  if (Array.isArray(ticket.assigned_to) && ticket.assigned_to.length) {
    return ticket.assigned_to.join(', ');
  }
  return 'Unassigned';
}

export function ticketProductLabel(ticket) {
  return ticket.domain_fields?.product_name || ticket.product_name || '';
}

const DOMAIN_CATEGORY_LABELS = {
  hardware: 'Hardware',
  software: 'Software',
  product: 'App Support',
};

/** Category column: use stored category/service, else domain (Hardware/Software). */
export function ticketCategoryLabel(ticket) {
  const cat = String(ticket?.category || '').trim();
  const svc = String(ticket?.service || '').trim();
  if (cat && svc) return `${cat} / ${svc}`;
  if (cat) return cat;
  if (svc) return svc;
  const domain = String(ticket?.domain || '').toLowerCase();
  return DOMAIN_CATEGORY_LABELS[domain] || (domain ? domain.charAt(0).toUpperCase() + domain.slice(1) : '—');
}

/** Normalize a domain_fields value (string or {label,value,name}). */
function fieldDisplayValue(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'object') {
    return String(raw.label || raw.name || raw.value || raw.text || '').trim();
  }
  return String(raw).trim();
}

/**
 * Pick first matching domain field by exact key, then case/space-insensitive key.
 */
function pickDomainField(domainFields, keys) {
  const df = domainFields && typeof domainFields === 'object' && !Array.isArray(domainFields)
    ? domainFields
    : {};
  for (const key of keys) {
    const val = fieldDisplayValue(df[key]);
    if (val) return val;
  }
  const lowered = {};
  Object.entries(df).forEach(([k, v]) => {
    lowered[String(k).toLowerCase().replace(/[\s-]+/g, '_')] = v;
  });
  for (const key of keys) {
    const val = fieldDisplayValue(lowered[String(key).toLowerCase()]);
    if (val) return val;
  }
  return '';
}

function asDomainFields(ticket) {
  let df = ticket?.domain_fields;
  if (typeof df === 'string') {
    try { df = JSON.parse(df); } catch { df = {}; }
  }
  return df && typeof df === 'object' && !Array.isArray(df) ? df : {};
}

const CLIENT_FIELD_KEYS = [
  'companies',
  'company_name',
  'client_name',
  'client',
  'company',
  'account_name',
  'company_or_office',
  'client_or_company',
];

const BRANCH_FIELD_KEYS = [
  'branch',
  'branch_name',
  'office_branch',
  'branch_or_office',
];

/** Client / company + branch from domain_fields (any domain) when present. */
export function ticketClientBranchLabel(ticket) {
  const df = asDomainFields(ticket);
  const wa = ticket?.whatsapp && typeof ticket.whatsapp === 'object' ? ticket.whatsapp : {};

  const client = pickDomainField(df, CLIENT_FIELD_KEYS)
    || fieldDisplayValue(wa.client_name)
    || fieldDisplayValue(wa.company)
    || fieldDisplayValue(ticket?.client_name)
    || fieldDisplayValue(ticket?.company)
    || fieldDisplayValue(ticket?.display_client)
    || '';

  const branch = pickDomainField(df, BRANCH_FIELD_KEYS)
    || fieldDisplayValue(wa.branch)
    || fieldDisplayValue(ticket?.branch_name)
    || fieldDisplayValue(ticket?.branch)
    || fieldDisplayValue(ticket?.display_branch)
    || '';

  if (client && branch) return `${client} / ${branch}`;
  if (client) return client;
  if (branch) return branch;
  return '—';
}

export function formatStatusLabel(status) {
  return STATUS_LABELS[status] || (status || '').replace(/_/g, ' ');
}
